// src/app/core/pwa/services/pwa.service.ts

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { PwaPreferences, PwaPromptEvent, PwaInstallState, PwaPromptStatus } from '../interfaces/pwa.interface';

type WindowWithDeferredPrompt = Window & { __pwaInstallPrompt?: PwaPromptEvent | null };

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  private readonly STORAGE_KEY = 'pwa_preferences';
  private readonly MAX_SHOW_COUNT = 3;
  private readonly MIN_USAGE_TIME = 30000; // 30 segundos

  private promptEvent: PwaPromptEvent | null = null;
  private installState = new BehaviorSubject<PwaInstallState>({
    canInstall: false,
    isInstalled: false,
    isStandalone: this.isRunningStandalone(),
    promptEvent: null
  });

  private promptStatus = new BehaviorSubject<PwaPromptStatus>(PwaPromptStatus.HIDDEN);

  constructor() {
    this.initPwaPrompt();
    this.checkInstallationStatus();
  }

  /**
   * Observable para o estado de instalação
   */
  get installState$(): Observable<PwaInstallState> {
    return this.installState.asObservable();
  }

  /**
   * Observable para o status do prompt
   */
  get promptStatus$(): Observable<PwaPromptStatus> {
    return this.promptStatus.asObservable();
  }

  /**
   * Inicializa os event listeners para PWA
   */
  private initPwaPrompt(): void {
    // El evento puede haberse disparado antes del bootstrap de Angular;
    // el script inline de index.html lo guarda en window.__pwaInstallPrompt.
    const deferred = (window as WindowWithDeferredPrompt).__pwaInstallPrompt;
    if (deferred) {
      this.promptEvent = deferred;
      this.updateInstallState({ canInstall: true, promptEvent: deferred });
      console.log('PWA Service: Recovered beforeinstallprompt captured before bootstrap');
    }

    // Listener para o evento beforeinstallprompt
    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      const promptEvent = event as unknown as PwaPromptEvent;
      this.promptEvent = promptEvent;
      console.log('PWA Service: beforeinstallprompt captured');
      this.updateInstallState({
        canInstall: true,
        promptEvent
      });
    });

    // Listener para quando o app é instalado
    window.addEventListener('appinstalled', () => {
      this.promptEvent = null;
      (window as WindowWithDeferredPrompt).__pwaInstallPrompt = null;
      this.updateInstallState({
        canInstall: false,
        isInstalled: true,
        promptEvent: null
      });
      this.savePreferences({ installed: true });
      this.promptStatus.next(PwaPromptStatus.HIDDEN);
    });
  }

  /**
   * Verifica se o app está rodando em modo standalone
   */
  isRunningStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches || 
           (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  }

  /**
   * Verifica se pode mostrar o prompt de instalação
   */
  canShowPrompt(): boolean {
    const preferences = this.getPreferences();
    const state = this.installState.value;

    // Não mostrar se já está instalado ou rodando standalone
    if (state.isInstalled || state.isStandalone) {
      return false;
    }

    // Não mostrar se não pode instalar
    if (!state.canInstall || !this.promptEvent) {
      return false;
    }

    // Não mostrar se usuário já recusou
    if (preferences.dismissed) {
      return false;
    }

    // Não mostrar se já atingiu o limite
    if (preferences.showCount >= this.MAX_SHOW_COUNT) {
      return false;
    }

    // Verificar se passou tempo suficiente desde a última exibição
    if (preferences.lastShown) {
      const timeSinceLastShown = Date.now() - new Date(preferences.lastShown).getTime();
      const minTimeBetweenShows = 24 * 60 * 60 * 1000; // 24 horas
      if (timeSinceLastShown < minTimeBetweenShows) {
        return false;
      }
    }

    return true;
  }

  /**
   * Mostra o popup de instalação (apenas UI)
   */
  showPromptUI(): void {
    if (this.canShowPrompt()) {
      this.promptStatus.next(PwaPromptStatus.VISIBLE);
      this.updateShowCount();
    }
  }

  /**
   * Executa o prompt de instalação nativo
   */
  async showInstallPrompt(): Promise<string> {
    if (!this.canShowPrompt() || !this.promptEvent) {
      throw new Error('Cannot show install prompt');
    }

    this.promptStatus.next(PwaPromptStatus.INSTALLING);

    try {
      await this.promptEvent.prompt();
      
      const { outcome } = await this.promptEvent.userChoice;
      
      if (outcome === 'accepted') {
        this.savePreferences({ installed: true });
      } else {
        this.savePreferences({ dismissed: true, dismissedAt: new Date() });
      }

      this.promptEvent = null;
      (window as WindowWithDeferredPrompt).__pwaInstallPrompt = null;
      this.updateInstallState({ canInstall: false, promptEvent: null });
      this.promptStatus.next(PwaPromptStatus.HIDDEN);

      return outcome;
    } catch (error) {
      this.promptStatus.next(PwaPromptStatus.HIDDEN);
      throw error;
    }
  }

  /**
   * Descarta o prompt (usuário clicou em "Agora não")
   */
  dismissPrompt(): void {
    this.promptStatus.next(PwaPromptStatus.DISMISSED);
    this.updateShowCount();
    
    // Não marcar como definitivamente recusado, apenas incrementar contador
    setTimeout(() => {
      this.promptStatus.next(PwaPromptStatus.HIDDEN);
    }, 300);
  }

  /**
   * Marca como definitivamente recusado (usuário clicou no X)
   */
  permanentlyDismiss(): void {
    this.savePreferences({ 
      dismissed: true, 
      dismissedAt: new Date() 
    });
    this.promptStatus.next(PwaPromptStatus.HIDDEN);
  }

  /**
   * Obtém as preferências do usuário
   */
  private getPreferences(): PwaPreferences {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          dismissed: parsed.dismissed || false,
          dismissedAt: parsed.dismissedAt ? new Date(parsed.dismissedAt) : null,
          showCount: parsed.showCount || 0,
          lastShown: parsed.lastShown ? new Date(parsed.lastShown) : null,
          installed: parsed.installed || false
        };
      } catch (error) {
        console.warn('Error parsing PWA preferences:', error);
      }
    }

    return {
      dismissed: false,
      dismissedAt: null,
      showCount: 0,
      lastShown: null,
      installed: false
    };
  }

  /**
   * Salva as preferências do usuário
   */
  private savePreferences(updates: Partial<PwaPreferences>): void {
    const current = this.getPreferences();
    const updated = { ...current, ...updates };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated));
  }

  /**
   * Atualiza o contador de exibições
   */
  private updateShowCount(): void {
    const preferences = this.getPreferences();
    this.savePreferences({
      showCount: preferences.showCount + 1,
      lastShown: new Date()
    });
  }

  /**
   * Atualiza o estado de instalação
   */
  private updateInstallState(updates: Partial<PwaInstallState>): void {
    const current = this.installState.value;
    this.installState.next({ ...current, ...updates });
  }

  /**
   * Verifica o status de instalação atual
   */
  private checkInstallationStatus(): void {
    const preferences = this.getPreferences();
    if (preferences.installed || this.isRunningStandalone()) {
      this.updateInstallState({
        isInstalled: true,
        isStandalone: this.isRunningStandalone()
      });
    }
  }

  /**
   * Reseta as preferências (útil para testes)
   */
  resetPreferences(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }
}
